//@flow

import React, {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {Box, Typography, CircularProgress, IconButton} from '@material-ui/core';
import {makeStyles} from '@material-ui/core';
import {
  Add as AddIcon,
  GetApp as ImportIcon,
  PlayCircleFilledTwoTone,
  HelpOutline as HelpOutlineIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import ProductOptionGroupInlineForm from './ProductOptionGroupInlineForm';
import ImportVariantsModal from './ImportVariantsModal';
import {useProductOptionGroups} from '../utils';
import {useNearScreen} from '../../../utils/react';
import {useProductVariants} from '../../variants/utils';
import {colors} from '../../../assets/styles/colors';

import type {CreateStoreProductOptionGroupRequest} from '../../../entities/product-option';
import {TIENDADA_LINKS} from '../../../constants/tiendada-links';
import TemporaryFeatureAlert from '../../entitlement/components/TemporaryFeatureAlert';

const useStyles = makeStyles(theme => ({
  root: {},
  header: {
    display: 'flex',
    marginBottom: '1rem',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.grey900,
    flex: 1,
  },
  headerButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    marginBottom: '1rem',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  addButton: {
    backgroundColor: colors.grey200,
    color: colors.grey700,
    borderStyle: 'dashed',
    borderWidth: '2px',
    borderColor: colors.grey400,
    padding: '.25rem 0.75rem',
    position: 'sticky',
    bottom: '1rem',
    '&:hover': {
      backgroundColor: colors.grey300,
      borderColor: colors.grey500,
    },
  },
  importButton: {
    padding: '.25rem 0.75rem',
    backgroundColor: colors.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: colors.primaryDark,
    },
  },
  groupsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: colors.grey600,
  },
  emptyStateIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
    color: colors.grey400,
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  tutorialsButton: {
    marginLeft: theme.spacing(2),
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      padding: 0,
      boxShadow: 'unset',
    },
  },
  timelineItem: {
    '&:before': {
      display: 'none',
    },
  },
  stepTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey900,
  },
  stepDescription: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  currentStep: {
    padding: '8px',
    borderRadius: '4px',
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: '0.8125rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '0.375rem 1.25rem',
    boxShadow: '0 2px 8px rgba(123, 57, 255, 0.2)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.purple4,
      boxShadow: '0 4px 12px rgba(123, 57, 255, 0.3)',
      transform: 'translateY(-1px)',
    },
  },
}));

type Props = {
  onBack?: () => void,
};

export default function ProductOptionGroupsList({onBack}: Props) {
  const classes = useStyles();
  const [newGroupId, setNewGroupId] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [helpModalOpen, setHelpModalOpen] = useState<boolean>(false);
  const [importedGroups, setImportedGroups] = useState<any[]>([]);

  const {
    items: groups,
    loading,
    loadMore,
    hasMore,
    refetch,
  } = useProductOptionGroups();

  const productVariants = useProductVariants();

  const {isNearScreen, fromRef} = useNearScreen({
    distance: '100px',
    once: false,
  });

  useEffect(() => {
    if (isNearScreen && hasMore && !loading) {
      loadMore();
    }
  }, [isNearScreen, hasMore, loading, loadMore]);

  const handleAddNewGroup = () => {
    const tempId = `new-${Date.now()}`;
    setNewGroupId(tempId);
  };

  const handleImportFromVariants = () => {
    setImportModalOpen(true);
  };

  const handleImportVariants = (
    convertedGroups: CreateStoreProductOptionGroupRequest[]
  ) => {
    const groupsWithIds = convertedGroups.map((group, index) => ({
      ...group,
      tempId: `imported-${Date.now()}-${index}`,
      isImported: true,
    }));

    setImportedGroups(groupsWithIds);
    setImportModalOpen(false);
  };

  const handleImportedGroupSaved = (tempId: string) => {
    setImportedGroups(prev => prev.filter(group => group.tempId !== tempId));
    refetch();
  };

  const handleImportedGroupCancelled = (tempId: string) => {
    setImportedGroups(prev => prev.filter(group => group.tempId !== tempId));
  };

  const handleNewGroupSaved = (group: any) => {
    setNewGroupId(null);
    refetch();
  };

  const handleNewGroupCancelled = () => {
    setNewGroupId(null);
  };

  const handleGroupSaved = (updatedGroup: any) => {
    refetch();
  };

  const handleGroupDeleted = () => {
    refetch();
  };

  const hasVariants = productVariants && productVariants.length > 0;

  if (
    loading &&
    groups.length === 0 &&
    !newGroupId &&
    importedGroups.length === 0
  ) {
    return (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            Opciones de producto
          </Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress color="primary" />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <TemporaryFeatureAlert
        feature="PRODUCT_OPTIONS"
        plans={['TIER_360', 'TIER_350']}
      />
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography className={classes.title}>
            Opciones de producto
          </Typography>
          <IconButton
            onClick={() => setHelpModalOpen(true)}
            size="small"
            style={{marginLeft: 8}}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Box>
        <CustomButton
          href={TIENDADA_LINKS.tutorials.PRODUCT_OPTIONS}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.tutorialsButton}
          startIcon={<PlayCircleFilledTwoTone />}
        >
          Tutoriales
        </CustomButton>
      </Box>
      <Box className={classes.headerButtons}>
        {(groups.length > 0 || importedGroups.length > 0) && (
          <CustomButton
            className={classes.addButton}
            onClick={handleAddNewGroup}
            startIcon={<AddIcon />}
            disabled={!!newGroupId}
            fullWidth
          >
            Agregar nuevo grupo
          </CustomButton>
        )}
        {hasVariants && (
          <CustomButton
            className={classes.importButton}
            onClick={handleImportFromVariants}
            startIcon={<ImportIcon />}
            variant="contained"
            fullWidth
          >
            Importar desde variantes
          </CustomButton>
        )}
      </Box>

      <Box className={classes.groupsList}>
        {groups.map(group => (
          <ProductOptionGroupInlineForm
            key={group.id}
            group={group}
            onSave={handleGroupSaved}
            onDelete={handleGroupDeleted}
          />
        ))}

        {importedGroups.map(group => (
          <ProductOptionGroupInlineForm
            key={group.tempId}
            isNew={true}
            prefilledData={group}
            onSave={() => handleImportedGroupSaved(group.tempId)}
            onCancel={() => handleImportedGroupCancelled(group.tempId)}
          />
        ))}

        {newGroupId && (
          <ProductOptionGroupInlineForm
            key={newGroupId}
            isNew={true}
            onSave={handleNewGroupSaved}
            onCancel={handleNewGroupCancelled}
          />
        )}

        {groups.length === 0 && !newGroupId && importedGroups.length === 0 && (
          <Box className={classes.emptyState}>
            <AddIcon className={classes.emptyStateIcon} />
            <Typography variant="h6" gutterBottom>
              No hay grupos de opciones
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Crea tu primer grupo de opciones para productos
            </Typography>

            <Box className={classes.actionsContainer}>
              <CustomButton
                className={classes.addButton}
                onClick={handleAddNewGroup}
                startIcon={<AddIcon />}
                fullWidth
              >
                Crear nuevo grupo
              </CustomButton>

              {hasVariants && (
                <CustomButton
                  className={classes.importButton}
                  onClick={handleImportFromVariants}
                  startIcon={<ImportIcon />}
                  variant="contained"
                  fullWidth
                >
                  Importar desde variantes de producto
                </CustomButton>
              )}
            </Box>
          </Box>
        )}

        {hasMore && (
          <Box ref={fromRef} style={{height: '1px', marginTop: '1rem'}} />
        )}

        {loading && groups.length > 0 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>

      <ImportVariantsModal
        open={importModalOpen}
        variants={productVariants || []}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImportVariants}
      />

      <CustomModal
        open={helpModalOpen}
        handleCloseModal={() => setHelpModalOpen(false)}
      >
        <Box>
          <div
            style={{
              textAlign: 'center',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: colors.grey900,
              marginBottom: '1rem',
            }}
          >
            Flujo de opciones de producto
          </div>
          <Timeline>
            <TimelineItem className={classes.timelineItem}>
              <TimelineSeparator>
                <TimelineDot color="primary">
                  <AddIcon />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box className={classes.currentStep}>
                  <div className={classes.stepTitle}>
                    Crear grupo de opciones
                  </div>
                  <div className={classes.stepDescription}>
                    Crea grupos de opciones globales aquí.
                  </div>
                </Box>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem className={classes.timelineItem}>
              <TimelineSeparator>
                <TimelineDot>
                  <CheckCircleIcon />
                </TimelineDot>
              </TimelineSeparator>
              <TimelineContent>
                <Box>
                  <div className={classes.stepTitle}>Agregar al producto</div>
                  <div className={classes.stepDescription}>
                    Ve al producto y agrega las opciones en la pestaña
                    correspondiente.
                  </div>
                  <Box mt={1}>
                    <CustomButton
                      variant="contained"
                      className={classes.upgradeButton}
                      component={Link}
                      to="/products/new?defaultSection=options"
                      size="small"
                    >
                      <ArrowForwardIcon />
                      Ir a Productos
                    </CustomButton>
                  </Box>
                </Box>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        </Box>
      </CustomModal>
    </Box>
  );
}
